import type { ReactNode } from 'react';
import { lazy, startTransition, Suspense, useEffect } from 'react';

import 'react-day-picker/style.css';
import { ApolloProvider } from '@apollo/client/react';
import { BricksProvider } from '@opendoor/bricks-next';
import { AddContextualData, addDatadogRUMGlobalContext } from '@opendoor/observability';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import '@js-joda/timezone/dist/js-joda-timezone-1970-2030';
import { AuthProvider } from 'platform/components/AuthContext';
import { TosFreeFullScreenLoader } from 'platform/components/FullScreenLoader';

import '@opendoor/primer/stylesheets/primer.css';
import { CustomerBoundary } from 'platform/CustomerBoundary';
import 'core-js/es';

import 'index.css';
import 'intl';

import 'mapbox-gl/dist/mapbox-gl.css';
import { ErrorHandler } from 'platform/ErrorHandler';
import { useApptoolbox } from 'platform/hooks/useApptoolbox';
import { PageStalenessTimeProvider } from 'platform/hooks/useReloadStalePage';
import { useLoadObservability, useObservability } from 'platform/observability';
import { ClientOnly } from 'platform/router/utils/ClientOnly';
import { TabVisibilityTrackerScript } from 'platform/TabVisibilityTrackerScript';
import { StyleSheet } from 'react-native';
import type { MetaFunction } from 'react-router';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { apolloClient } from 'setupClient';
import { IS_DEV } from 'shared/helpers/env';
import { useTrackInitialLoad } from 'shared/hooks/useTrackPerformance';
import Tamagui from 'tamagui.config';

// Lazy component must be created at module scope; creating it inside the
// component body produces a fresh lazy wrapper (and a fresh suspending
// Promise) on every render, which traps route transitions in a suspense loop.
const DevTools =
  IS_DEV && typeof window !== 'undefined'
    ? lazy(async () => {
        const mod = await import('react-router-devtools/client');
        return { default: mod.withViteDevTools((): null => null) };
      })
    : null;

// Injects devtools when we're in dev and clientside.
// Local Suspense with a null fallback keeps a devtools chunk miss from swapping
// the whole app to LoadingPage via the outer Suspense boundary.
function LazyDevTools(): JSX.Element | null {
  if (!DevTools) {
    return null;
  }

  return (
    <ClientOnly>
      {() => (
        <Suspense fallback={null}>
          <DevTools />
        </Suspense>
      )}
    </ClientOnly>
  );
}

function AppContents({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Wrapped in a startTransition to avoid blocking the main thread
    startTransition(() => {
      addDatadogRUMGlobalContext({ framework: 'react-router' });
    });
  }, []);
  useLoadObservability();
  useTrackInitialLoad();
  useApptoolbox();

  return (
    <AddContextualData data={{ framework: 'react-router' }}>
      <ApolloProvider client={apolloClient()}>
        <BricksProvider useObservability={useObservability} disableInjectCSS disableRootThemeClass>
          <Suspense fallback={<TosFreeFullScreenLoader />}>
            <ClientOnly fallback={<TosFreeFullScreenLoader />}>
              {() => <AuthProvider>{children}</AuthProvider>}
            </ClientOnly>
          </Suspense>
          <TabVisibilityTrackerScript />
          <LazyDevTools />
        </BricksProvider>
      </ApolloProvider>
    </AddContextualData>
  );
}

export const meta: MetaFunction = () => [{ title: 'Opendoor' }];

// RR7 Layout export wraps the default export, ErrorBoundary, and HydrateFallback.
// Rendered on both prerender and client, so React owns the full document and
// route-level <Meta />/<Links /> updates reach the DOM.
export function Layout({ children }: { children: ReactNode }) {
  const tamaguiCSS = Tamagui.getCSS();

  return (
    <html lang="en">
      <head>
        <Meta />
        <meta name="framework" content="react-router" />
        <meta charSet="utf-8" />
        <link rel="shortcut icon" href={faviconPath({ railsEnv: import.meta.env.ODFE_ENV })} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#FAF9F6" />
        <meta name="msapplication-navbutton-color" content="#FAF9F6" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#FAF9F6" />
        <Links />
        {/* Preload fonts to aid with CLS and LCP. URLs must match the @font-face
          src in index.css exactly so the browser reuses the preload. */}
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Bold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Semibold-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          href="https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2"
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <style
          id="react-native-stylesheet"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            // @ts-expect-error -- hakuna matata
            __html: StyleSheet.getSheet().textContent,
          }}
        />
        <style suppressHydrationWarning dangerouslySetInnerHTML={{ __html: tamaguiCSS }} />
      </head>
      <body id="root">
        <AppContents>{children}</AppContents>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <CustomerBoundary>
      <PageStalenessTimeProvider>
        <Outlet />
      </PageStalenessTimeProvider>
    </CustomerBoundary>
  );
}

/**
 * Catches errors anywhere in its child component tree and renders a fallback component instead of the component that crashed.
 */
export function ErrorBoundary() {
  return <ErrorHandler />;
}

export function HydrateFallback() {
  return <TosFreeFullScreenLoader />;
}
